import type { ReviewResponse } from "@joogoos/contracts";
import { ApiError } from "../api.js";
import { KICKER } from "../tabs/chrome.js";
import { money, onDay, useLate, useStartReview, useStarting, who } from "./Review.js";

/**
 * The marketing review's door on the Overview ([35](../../../../docs/35-the-marketing-manager.md), ADR 0063), at the
 * top of the welcome and of the Overview alike: the review is the loop the product is built around, so it is the
 * first thing either screen says, in one card.
 *
 * With no review yet it is the paid press itself, with its price beside it — the way Prepare my week is on the Studio
 * rail — and once the review is written it opens it. After that it is a door: the recommendation waiting for a
 * decision, or what was decided and what came of it. It reads what the Overview already read and asks for nothing of
 * its own, so it costs the Overview no round trip (docs/17 §performance 4).
 *
 * Its button is ink, never coral: the coral on the Overview is the one thing that needs you most, and a decision
 * waiting on the review is put on the owner's list by the Overview itself. Anybody else is told here that it waits
 * for the owner, because it is not theirs to give.
 *
 * It draws nothing on a box that cannot write a review, and nothing while the read is out or failed: the Overview is
 * not the place to explain a feature that is not there.
 */
export function ReviewEntry({ review, onOpen, className = "" }: {
  review: ReviewResponse | undefined;
  /** The review screen, `/overview/review`. */
  onOpen: () => void;
  className?: string;
}) {
  const start = useStartReview();
  const starting = useStarting();
  // Before the door below closes on a box with no review, because a hook is asked for on every draw or on none.
  const late = useLate(review?.latest);
  if (!review?.available) return null;
  const latest = review.latest;
  const decision = latest?.decision ?? null;
  const action = decision?.action ?? latest?.body?.actions[0] ?? null;
  const go = (label: string) => (
    <button onClick={onOpen} className="shrink-0 px-3.5 py-2 rounded-lg bg-ink text-white text-[12.5px] font-semibold hover:opacity-90">
      {label} <span aria-hidden="true">→</span>
    </button>
  );
  const begin = (label: string) => review.canStart.ok ? (
    <span className="shrink-0 flex items-center gap-2.5">
      {review.priceUsd ? <span className="text-[11.5px] text-faint tabular-nums">about {money(review.priceUsd)}</span> : null}
      {/* Once it is written, the review is opened — but only from here: somebody who has gone to another tab while
          it was being written is not pulled back to it. */}
      <button
        onClick={() => start.mutate(undefined, { onSuccess: onOpen })}
        disabled={start.isPending}
        className="px-3.5 py-2 rounded-lg bg-ink text-white text-[12.5px] font-semibold hover:opacity-90 disabled:opacity-50"
      >
        {label}
      </button>
    </span>
  ) : <span className="text-[12px] text-muted max-w-[36ch]">{review.canStart.why}</span>;

  let kicker = "Marketing review";
  let line: string;
  let note: string | null;
  let door: React.ReactNode;
  if (late && !start.isPending && !starting) {
    line = "Your review is taking longer than it should.";
    note = "Come back in a few minutes: one review is only ever charged once.";
    door = null;
  } else if (start.isPending || starting || latest?.status === "writing") {
    line = "Reading your week…";
    note = "Your website, your letters and your posts over the last seven days, and the searches over the four weeks Google last reported. It takes about a minute.";
    door = null;
  } else if (!latest) {
    line = "What should you do next?";
    note = "Joogoos reads your last seven days and proposes one thing to do, with what it costs and how it is counted. Nothing is done until you say yes.";
    door = begin("Review my marketing");
  } else if (latest.status === "failed") {
    line = "Your last review could not be written.";
    note = latest.error;
    door = begin("Try again");
  } else if (decision?.state === "proposed" && action) {
    kicker = `Marketing review · ${latest.evidence.window.label}`;
    line = action.title;
    // The decision is the owner's alone (`mayDecide`): anybody else is told whose it is, and what they can do meanwhile.
    note = review.mayDecide
      ? "Yours to decide: say yes, change it, or say no. Saying yes spends nothing."
      : "Waiting for the owner's decision. You can read it and ask about it.";
    door = go("Read the review");
  } else if (decision?.state === "approved" && action) {
    line = `${who(review.mayDecide)} said yes: ${action.title}`;
    note = decision.outcome?.says ?? (decision.reviewOn ? `It is looked at again on ${onDay(decision.reviewOn)}.` : null);
    door = go("Read the review");
  } else if (decision?.state === "rejected" && action) {
    line = `${who(review.mayDecide)} said no: ${action.title}`;
    note = "The next review is told, so it does not propose the same again.";
    door = go("Read the review");
  } else {
    line = "Your marketing review";
    note = null;
    door = go("Read the review");
  }

  return (
    <section aria-label="Marketing review" className={`rounded-xl border border-line bg-panel px-4 py-3.5 ${className}`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-[280px]">
          <h2 className={`${KICKER} text-muted`}>{kicker}</h2>
          <p className="mt-1 text-[15px] font-semibold tracking-[-.01em] text-balance">{line}</p>
          {note ? <p className="mt-0.5 text-[12.5px] text-muted leading-relaxed max-w-[64ch]">{note}</p> : null}
        </div>
        {door}
      </div>
      {/* A refusal the review was stored with is already the note above; it is not said twice. */}
      {start.error && start.error.message !== latest?.error ? (
        <p role="alert" className="mt-2 text-[12.5px] text-bad leading-relaxed">
          {start.error.message}
          {start.error instanceof ApiError && start.error.ref ? <span className="opacity-70 font-mono"> · ref {start.error.ref}</span> : null}
        </p>
      ) : null}
    </section>
  );
}
